import React, { useState, useEffect, useMemo } from 'react';
import LightGallery from 'lightgallery/react';
import 'lightgallery/css/lightgallery.css';
import 'lightgallery/css/lg-zoom.css';
import 'lightgallery/css/lg-thumbnail.css';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgZoom from 'lightgallery/plugins/zoom';
import { Modal, ModalBody } from 'reactstrap';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Attachment } from '../../models/Attachment.interface';
import './AttachmentGallery.css';
import AttachmentGalleryModal from './AttachmentGalleryModal';

interface AttachmentGalleryProps {
  attachments: Attachment[];
}

const AttachmentGallery: React.FC<AttachmentGalleryProps> = ({ attachments }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState<Attachment | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [galleryModalOpen, setGalleryModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Filter out invalid attachments and blob URLs
  const validAttachments = useMemo(() => {
    console.log("Filtering attachments:", attachments);
    return Array.isArray(attachments) ? attachments.filter(
      attachment => 
        attachment && 
        typeof attachment === 'object' && 
        typeof attachment.url === 'string' && 
        !attachment.url.startsWith('blob:')
    ) : [];
  }, [attachments]);

  console.log("Valid attachments:", validAttachments);

  const getFileType = (url: string): string => {
    const fileName = url.split('/').pop()?.split('?')[0] || '';
    const ext = fileName.split('.').pop()?.toLowerCase() || '';

    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
    if (['mp4', 'webm', 'mov'].includes(ext)) return 'video';
    if (ext === 'pdf') return 'pdf';
    if (ext === 'heic') return 'heic';
    return 'other';
  };

  const renderPreview = (attachment: Attachment) => {
    const type = getFileType(attachment.url);

    switch (type) {
      case 'image':
      case 'heic':
        return (
          <img
            src={attachment.url}
            alt={attachment.caption || ''}
            className="attachment-preview-image"
            onError={(e) => {
              console.error('Image failed to load:', attachment.url);
              // Set a fallback image
              (e.target as HTMLImageElement).src = 'data:image/svg+xml;charset=utf-8,%3Csvg xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22 viewBox%3D%220 0 36 36%22%3E%3Crect width%3D%2236%22 height%3D%2236%22 fill%3D%22%23e2e8f0%22%2F%3E%3Ctext x%3D%2218%22 y%3D%2218%22 font-size%3D%2214%22 fill%3D%22%23718096%22 text-anchor%3D%22middle%22 alignment-baseline%3D%22middle%22%3EImage%3C%2Ftext%3E%3C%2Fsvg%3E';
            }}
          />
        );
      case 'video':
        return (
          <video controls className="attachment-preview-video">
            <source src={attachment.url} type={`video/${type}`} />
            Your browser does not support video playback
          </video>
        );
      case 'pdf':
        return (
          <object
            data={attachment.url}
            type="application/pdf"
            className="attachment-preview-pdf"
          >
            <div className="pdf-fallback">
              <a href={attachment.url} target="_blank" rel="noopener noreferrer">
                View PDF
              </a>
            </div>
          </object>
        );
      default:
        return (
          <div className="attachment-preview-other">
            <a href={attachment.url} target="_blank" rel="noopener noreferrer">
              Open Attachment
            </a>
          </div>
        );
    }
  };

  const handleAttachmentClick = (index: number) => {
    setSelectedIndex(index);
    setGalleryModalOpen(true);
  };

  // Show only first few attachments
  const visibleAttachments = validAttachments.slice(0, 7);

  const galleryContent = modalOpen && selectedAttachment && (
    <LightGallery
      speed={500}
      plugins={[lgThumbnail, lgZoom]}
      elementClassNames="gallery-container"
    >
      {validAttachments.map((attachment) => (
        <a
          href={attachment.url}
          className="gallery-item"
          key={attachment.id || attachment.url}
          data-src={attachment.url}
        >
          {renderPreview(attachment)}
        </a>
      ))}
    </LightGallery>
  );

  const checkImageOrientation = async (url: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve(img.height > img.width);
      };
      img.onerror = () => {
        console.error('Failed to load image for orientation check:', url);
        resolve(false);
      };
      img.src = url;
    });
  };

  const handlePrevious = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIndex((prev) => (prev > 0 ? prev - 1 : validAttachments.length - 1));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIndex((prev) => (prev < validAttachments.length - 1 ? prev + 1 : 0));
  };

  const [orientations, setOrientations] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const loadOrientations = async () => {
      const orientationMap: Record<string, boolean> = {};
      for (const attachment of validAttachments) {
        if (getFileType(attachment.url) === 'image') {
          orientationMap[attachment.id || attachment.url] = await checkImageOrientation(attachment.url);
        }
      }
      setOrientations(orientationMap);
    };
    loadOrientations();
  }, [validAttachments]);

  // Calculate grid columns based on number of attachments
  const getGridColumns = (count: number): string => {
    switch (count) {
      case 1:
        return '1fr';
      case 2:
        return '1fr 1fr';
      case 3:
        return '2fr 1fr';
      case 4:
        return '2fr 1fr 1fr';
      case 5:
        return '2fr 1fr 1fr';
      case 6:
        return '2fr 1fr 1fr';
      case 7:
        return '2fr 1fr 1fr 1fr';
      default: // 8 or more
        return '2fr 1fr 1fr 1fr';
    }
  };

  // If no valid attachments, show a message
  if (validAttachments.length === 0) {
    return (
      <div className="attachment-gallery">
        <div className="no-attachments-message">
          No attachments available
        </div>
      </div>
    );
  }

  return (
    <div className="attachment-gallery">
      <div 
        className="attachment-grid"
        style={{
          gridTemplateColumns: getGridColumns(validAttachments.length)
        }}
      >
        {visibleAttachments.map((attachment, index) => (
          <div
            key={attachment.id || index}
            className="attach-gallery-item"
            onClick={() => handleAttachmentClick(index)}
          >
            <div 
              className="attachment-thumbnail" 
              style={{ 
                backgroundImage: `url(${attachment.url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                width: '100%',
                height: '100%',
                cursor: 'pointer'
              }}
            ></div>
            {attachment.caption && (
              <div className="attachment-caption">{attachment.caption}</div>
            )}
          </div>
        ))}
      </div>

      <AttachmentGalleryModal
        isOpen={galleryModalOpen}
        toggle={() => setGalleryModalOpen(false)}
        attachments={validAttachments}
        initialIndex={selectedIndex}
      />
    </div>
  );
};

export default AttachmentGallery;